import { Suspense, useEffect } from 'react';
import { Outlet, useRouter } from '@tanstack/react-router';

import { RoutePending } from './routing/route-boundaries';
import { RouteAnnouncer } from './routing/route-announcer';
import { DevToolsLoader } from './dev-tools-loader';
import { SessionTracker } from './session/session-tracker';
import { SettingsDialog } from '@widgets/settings-dialog';
import {
	AppMenuConfigContext,
	type AppMenuItemKey,
} from '@shared/ui/app-menu';

declare global {
	interface Window {
		onbeforeinstallprompt?: unknown;
	}
}

/**
 * Shared root route component for every template. `sessionTracker` is the
 * only per-template opt-in left here -- devtools access (`DevToolsLoader`)
 * is unconditional and universal now that it costs nothing until
 * `window.toggleDevTools()` is actually called.
 */
export function RootRouteComponent({
	sessionTracker,
	appMenu,
}: {
	sessionTracker?: boolean;
	appMenu?: Array<AppMenuItemKey>;
} = {}) {
	const router = useRouter();
	return (
		<AppMenuConfigContext.Provider value={appMenu ?? []}>
			{sessionTracker && <SessionTracker />}
			{/* Defense-in-depth for topic 08's `useSuspenseQuery` conversion:
			    a route-loader-backed query (see `route-catalog.tsx`) never
			    actually suspends here, since `ensureQueryData` already resolved
			    it before this tree mounts -- this boundary exists for the case
			    that isn't loader-backed (a query key change, e.g. selecting a
			    different issue, with nothing pre-warmed) so a genuine suspend
			    shows the same pending UI as a loader-pending route, not a blank
			    screen with no ancestor Suspense boundary to catch it. */}
			<Suspense fallback={<RoutePending />}>
				<Outlet />
			</Suspense>
			{/* One instance for every route: `?settings=<page>` opens it over
			    whatever is showing (see @shared/lib/settings-route). */}
			<SettingsDialog />
			<RouteAnnouncer />
			<DevToolsLoader router={router} />
		</AppMenuConfigContext.Provider>
	);
}

export function NotFound() {
	const { navigate } = useRouter();

	useEffect(() => {
		navigate({ to: '/' }); // Redirect to homepage
	}, [navigate]);

	return null;
}
